import React, { useCallback } from 'react';
import { useFormik } from 'formik';
import dayjs from '@/shared/helpers/dayjs';
import * as Yup from 'yup';
import { PREGNANCY_OPTIONS } from '../../../constants';

// Components
import { Button, RadioGroup, Alert } from '@/shared/ui';
import { Box, useTheme } from '@mui/material';
import { SplitLayout } from '@/modules/onboarding/components/split-layout';
import {
    AllergiesAndConditionsSideComponent,
    Illustration,
    HIPAAComponent
} from '@/modules/onboarding/pages/about-you/components/allergies-and-conditions';

export interface PregnancyDefaultValuesInterface {
    pregnant: boolean | null;
    dob: string | Date;
}
interface AboutYouPregnancyFormProps {
    onFormSubmit: (values: PregnancyDefaultValuesInterface) => void;
    onGoBack: () => void;
    defaultValues: PregnancyDefaultValuesInterface;
}
interface AboutYouPregnancyProps {
    formArttributes: AboutYouPregnancyFormProps;
}

const validationSchema = Yup.object().shape({
    pregnant: Yup.boolean()
        .required('Please select an option')
        .test('age-limit', 'You must be no older than 55 years old to register as pregnant', function (value) {
            const birthDate = dayjs(this.parent.dob);
            return !(value === true && birthDate.add(55, 'years').isBefore(dayjs()));
        })
});

export const AboutYouPrangancyForm: React.FC<AboutYouPregnancyFormProps> = props => {
    const theme = useTheme();

    const formik = useFormik({
        initialValues: {
            pregnant: props.defaultValues.pregnant,
            dob: props.defaultValues.dob
        },
        validationSchema,
        onSubmit: values => {
            props.onFormSubmit(values);
        }
    });

    const handlePregnancySelect = useCallback(
        (value: string | number | boolean) => {
            const val: boolean = value.toString().toLowerCase() === 'true' ? true : false;
            formik.setFieldValue('pregnant', val, true);
        },
        [formik]
    );

    return (
        <>
            <Box
                data-testid="pregnancy-form"
                component="form"
                onSubmit={e => {
                    e.preventDefault();
                    formik.handleSubmit(e);
                }}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    [theme.breakpoints.down('lg')]: {
                        gap: 1
                    }
                }}
            >
                <div className="flex w-full flex-col gap-2 rounded-lg bg-white">
                    <section className="allergies-section aboutyou-form-sections">
                        <h3 data-testid="about-you-pregnancy-form-title" className="onboarding-section-title md:onboarding-section-title-lg w-full text-left">Are you pregnant?</h3>
                        <div className="aboutyou-form-section-content flex w-full flex-col gap-2 rounded-[0.8125rem]">
                            <div className="about-you-checkbox flex-no-wrap flex flex-col gap-2">
                                <RadioGroup
                                    items={PREGNANCY_OPTIONS}
                                    name="pregnant"
                                    dataTestId="pregnant"
                                    radioGroupClassName="allergies-and-conditions-radio-group flex-row"
                                    buttonClassName="allergies-and-conditions-radio-button"
                                    value={formik.values.pregnant}
                                    onSelectChange={handlePregnancySelect}
                                />
                            </div>
                        </div>
                    </section>
                    {formik.errors.pregnant && <Alert testId="pregnancy-form-error-message" label={formik.errors.pregnant as string} />}
                </div>
                <Button testId="button-pregnancy-continue" className="pregnancy__save_btn" type="submit" variant="contained" color="primary" showEndIcon>
                    Save & Continue
                </Button>
                <Button
                    className="pregnancy__back_btn"
                    variant="outlined"
                    color="primary"
                    onClick={() => props.onGoBack && props.onGoBack()}
                    testId="button-pregnancy-back"
                >
                    Back
                </Button>
                <section className="conditions-section aboutyou-form-sections mt-[12px]">
                    <HIPAAComponent />
                </section>
            </Box>
        </>
    );
};

const AboutYouPregnancyComponent: React.FC<AboutYouPregnancyProps> = props => {
    return (
        <SplitLayout
            sideContent={<AllergiesAndConditionsSideComponent />}
            mainContent={
                <div data-testid="about-you-pregnancy-container" className="w-full">
                    <div className="w-full">
                        <div className="box-wrapper desktop-min:box-wrapper-lg bg-white desktop-min:mb-[14.25rem]">
                            <AboutYouPrangancyForm {...props.formArttributes} />
                        </div>
                    </div>
                    <div className="mb-[14.7rem] mt-[2.75rem] desktop-min:mb-0 desktop-min:mt-0 desktop-min:hidden">
                        <Illustration />
                    </div>
                </div>
            }
        />
    );
};

export default AboutYouPregnancyComponent;
